import { LocalAudioTrack } from '../track/local-audio-track';
import { LocalScreenAudioTrack } from '../track/local-screen-audio-track';
import { IRoom } from '../enums';
import { AudioCodecPipelineType, ErrorCode, ITrack, Logger, RemoteAudioTrack, RemoteTrack, RoomEvent, RtcError, TrackEvent, getNetworkTime, loggerManager } from '../index';
import { startGetPCM } from './dump';
import { Track } from '../track/track';
import { AudioNodeConnector, AudioSource, MixContext, createAudioNode } from './audio-mixer';
import { isUndefined } from '../utils/utils';
import { getAudioContext } from '../utils/audio-context';
import innerEmitter from '../inner-emitter/inner-emitter';
import { INNER_EVENT } from '../inner-emitter/inner-type';
import { InnerEventTypes } from '../inner-emitter/inner-event';
import { CHROME_MAJOR_VERSION, CHROME_VERSION, isVersionSmallerThan } from '../utils/environment';

export enum AudioReferenceChangeType {
  ADD =  'add',
  REMOVE = 'remove',
  UPDATE_VOLUME = 'updateVolume'
}
export interface AudioEncodeProcessorParam {
  frame: RTCEncodedAudioFrame,
  ntp?: number
  onDump?: () => void
}
export interface AudioDecodeProcessorParam {
  frame: RTCEncodedAudioFrame,
  track?: RemoteAudioTrack, // script transform 没有 track 参数
  onDump?: () => void,
  onAudioFrameNTPTime?: (param: { ntp: number, frame: RTCEncodedAudioFrame, hasLeavingTag: boolean }) => void
}

export type AudioEncodeProcessor = ({ frame }: AudioEncodeProcessorParam) => RTCEncodedAudioFrame;
export type AudioDecodeProcessor = ({ frame }: AudioDecodeProcessorParam) => RTCEncodedAudioFrame | void;

export class AudioManager extends MixContext {
  // 麦克风
  private _localAudioTrack?: LocalAudioTrack;
  // 屏幕分享
  private _localScreenAudioTrack?: LocalScreenAudioTrack;
  log: Logger;
  denoiser?: AudioNode;
  voiceChanger?: [AudioNode, AudioNode];
  mixChangedDebounce?: Promise<any>;
  audioProcessor?: { localAudioWorkletNode?: AudioNode, mixNode: AudioNode, silentNode: AudioNode, screenAudioWorkletNode?: AudioNode };
  encodePipeline: (AudioEncodeProcessor | undefined)[] = [];
  decodePipeline: (AudioDecodeProcessor | undefined)[] = [];
  getPCMAbortCtrlMap: Map<string, AbortController | undefined> = new Map();
  audioFrameEventConfigMap: Map<string, { enable: boolean; sampleRate?: number; channelCount?: number; port?: MessagePort }> = new Map(); // '' 表示麦克风, '*' 表示所有远端音频, 其他表示某个远端音频
  audioReferenceMap: Map<string, AudioSource> = new Map(); // 回声消除的播放参考信号. 包括: 远端用户音频, 混音插件播放的音频，用户自己播放的音频
  isLocalAudioNeedAudioProcess = false;
  isScreenAudioNeedAudioProcess = false;
  constructor(public room?: IRoom) {
    super();
    this.log = loggerManager.createLogger({
      parent: room?.getLogger(),
      id: 'am',
      userId: room?.userId,
      sdkAppId: room?.sdkAppId
    });
    this.installEvent();
  }

  // 判断是否有混音：麦克风 + 混音插件 / 系统音频。不能用 mixWeight 判断，mixWeight 在设置 captureVolume 也会大于 1
  get isAudioMixed() {
    return this.inputs.size > 1;
  }
  get localAudioTrack() {
    return this._localAudioTrack;
  }
  get _localAudioPipline() {
    return this._localAudioTrack?.pipeline;
  }
  get _localScreenAudioPipeline() {
    return this._localScreenAudioTrack?.pipeline;
  }
  dump(dur: number) {
    if (!this._localAudioTrack) return;
    const sources: AudioNode[] = [];
    const fileName: string[] = [];
    if (this._localAudioPipline?.source.node) {
      sources.push(this._localAudioPipline.source.node);
      fileName.push('mic');
    }
    if (this._localAudioPipline?.denoiser.node) {
      sources.push(this._localAudioPipline.denoiser.node);
      fileName.push('mic-processed');
    }
    if (this.mixWeight > 1) {
      sources.push(this.audioContext.createMediaStreamSource(this._localAudioPipline!.stream!));
      fileName.push('mix');
    }
    this.log.info(`dump audio track ${fileName}, duration: ${dur}`);
    const abortCtrl = new AbortController();
    const datas: Float32Array[][] = [];
    const tid = setTimeout(() => {
      this.log.info('dump audio track complete please input "download()" to download.');
      abortCtrl.abort('timeout');
    }, dur * 1000);
    const download = () => {
      for (let i = 0; i < fileName.length; i++) {
        const fileUrl = URL.createObjectURL(new Blob(datas[i]));
        const downloadLink = document.createElement('a');
        downloadLink.href = fileUrl;
        downloadLink.download = `${fileName[i]}.pcm`;
        downloadLink.style.display = 'none';
        document.body.appendChild(downloadLink);
        downloadLink.click();
        URL.revokeObjectURL(fileUrl);
        downloadLink.remove();
      }
      clearTimeout(tid);
      abortCtrl.abort('download');
    };
    const promise = startGetPCM(sources).then(x => x.pipeTo(new WritableStream({
      write(chunk) {
        chunk.forEach((item, i) => datas[i] = datas[i] ? datas[i].concat(item[0]) : [item[0]]);
      }
    }), abortCtrl).catch(e => download));
    return {
      then: promise.then.bind(promise),
      download
    };
  }
  getPCM(gotPCMCallback: (audioFrameCallbackData: {userId: string, sampleRate: number, channelCount: number, data: Float32Array | Float32Array[]}) => void, userId: string) {
    if (typeof WritableStream === 'undefined') {
      this.log.warn('getPCM failed: browser not support WritableStream');
      return;
    }
    const { enable, sampleRate = 48000, channelCount = 1, port } = (userId === '' ? this.audioFrameEventConfigMap.get('') : this.audioFrameEventConfigMap.get(userId) || this.audioFrameEventConfigMap.get('*')) || {};
    if (!enable) return;
    this.log.info(`getPCM ${userId || 'local'}`);
    const datasLength = Math.floor(sampleRate * 0.04); // 40ms 抛出一次事件
    let leftDatas: Float32Array = new Float32Array(datasLength);
    let rightDatas: Float32Array = new Float32Array(datasLength);
    let remainLeftDatas: Float32Array | undefined;
    let remainRightDatas: Float32Array | undefined;
    let offset = 0;
    const abortCtrl = new AbortController();
    const track = userId === '' ? this._localAudioTrack?.mediaTrack : this.room?.remotePublishedUserMap.get(userId)?.remoteAudioTrack.mediaTrack;
    if (!track) {
      this.log.info(`getPCM failed: ${userId || 'local'} has no audio track`);
      return;
    }
    const source = getAudioContext().createMediaStreamSource(new MediaStream([track]));
    startGetPCM([source], sampleRate, channelCount, port).then(x => x.pipeTo(new WritableStream({
      write(chunk) {
        if (!chunk[0][0]) return; // Firefox 在刚开始可能没数据
        if (offset + chunk[0][0].length > datasLength) {
          leftDatas.set(chunk[0][0].subarray(0, datasLength - offset), offset);
          remainLeftDatas = chunk[0][0].subarray(datasLength - offset);
          if (chunk[0][1]) {
            rightDatas.set(chunk[0][1].subarray(0, datasLength - offset), offset);
            remainRightDatas = chunk[0][1].subarray(datasLength - offset);
          }
          offset += datasLength - offset;
        } else {
          if (remainLeftDatas) {
            leftDatas.set(remainLeftDatas, offset);
            offset += remainLeftDatas.length;
            remainLeftDatas = undefined;
          }
          if (remainRightDatas) {
            rightDatas.set(remainRightDatas, offset);
            remainRightDatas = undefined;
          }
          leftDatas.set(chunk[0][0], offset);
          if (chunk[0][1]) rightDatas.set(chunk[0][1], offset);
          offset += chunk[0][0].length;
        }
        if (offset >= datasLength) {
          offset = 0;
          gotPCMCallback({ userId, sampleRate, channelCount, data: channelCount === 1 ? leftDatas : [leftDatas, rightDatas] });
          leftDatas = new Float32Array(datasLength);
          rightDatas = new Float32Array(datasLength);
        }
      }
    }), abortCtrl).catch(error => this.log.warn(`stop getPCM reason:${error}`)));

    return abortCtrl;
  }
  get hasScreenAudioTrack() {
    return !isUndefined(this._localScreenAudioTrack);
  }
  get hasAudioTrack() {
    return !isUndefined(this._localAudioTrack);
  }
  changeInput(audioTrack: Track<'audio'>) {
    if (audioTrack instanceof LocalScreenAudioTrack) {
      this._localScreenAudioTrack = audioTrack;
      if (this.isScreenAudioNeedAudioProcess && this.audioProcessor?.screenAudioWorkletNode) {
        audioTrack.addAudioProcessor(this.audioProcessor.screenAudioWorkletNode, this.audioProcessor.mixNode, this.audioProcessor.silentNode);
        this.audioReferenceMap.forEach((audioReference, refId) => {
          audioTrack.mixAudioReference(audioReference, refId);
        });
      }
      audioTrack.pipeline.connect();
      return this.mixOnChange();
    }
    if (audioTrack instanceof LocalAudioTrack) {
      this._localAudioTrack = audioTrack;
      if (this.denoiser) audioTrack.addDenoiser(this.denoiser);
      if (this.isLocalAudioNeedAudioProcess && this.audioProcessor?.localAudioWorkletNode) {
        audioTrack.addAudioProcessor(this.audioProcessor.localAudioWorkletNode, this.audioProcessor.mixNode, this.audioProcessor.silentNode);
        this.audioReferenceMap.forEach((audioReference, refId) => {
          audioTrack.mixAudioReference(audioReference, refId);
        });
      }
      audioTrack.pipeline.connect();
      return this.mixOnChange();
    }
    if (audioTrack instanceof RemoteAudioTrack) {
      return audioTrack.setOutputMediaStreamTrack(audioTrack.mediaTrack!);
    }
  }

  mixAudioReference(source: AudioSource, userId: string) {
    this._localAudioTrack?.mixAudioReference(source, userId);
  }

  unMixAudioReference(sourceId: string) {
    this._localAudioTrack?.unMixAudioReference(sourceId);
  }

  setAudioReferenceVolume(sourceId: string, volume: number) {
    this._localAudioTrack?.setAudioReferenceVolume(sourceId, volume);
  }

  mixOnChange() {
    if (!this.mixChangedDebounce) {
      // TODO: 优化异步防抖实现 @israeli
      this.mixChangedDebounce = Promise.resolve().then(() => {
        delete this.mixChangedDebounce;
        return Promise.all([
          this._localAudioTrack?.setOutputMediaStreamTrack(this.mixWeight > 1 ? this.mixTrack : this._localAudioTrack.mediaTrack!),
          this._localScreenAudioTrack?.setOutputMediaStreamTrack(this.mixWeight > 1 ? this.mixTrack : this._localScreenAudioTrack.mediaTrack!)
        ]);
      });
    }
    return this.mixChangedDebounce;
  }
  onAudioMixedChanged(isAudioMixed: boolean) {
    this._localAudioTrack?.onAudioMixedChanged(isAudioMixed);
  }
  removeInput(track: ITrack) {
    if (track instanceof LocalScreenAudioTrack) {
      delete this._localScreenAudioTrack;
    } else if (track instanceof LocalAudioTrack) {
      delete this._localAudioTrack;
    } else if (track instanceof RemoteAudioTrack) {
    }
  }
  // 音频前处理: denoiser
  addDenoiser(node: AudioNode) {
    this.denoiser = node;
    this._localAudioTrack?.addDenoiser(node);
  }
  // 音频前处理: echo cancellation
  addAudioProcessor(mixNode: AudioNode, silentNode: AudioNode, localAudioWorkletNode?: AudioNode, screenAudioWorkletNode?: AudioNode) {
    this.audioProcessor = { localAudioWorkletNode, mixNode, silentNode, screenAudioWorkletNode };
    if (this.isLocalAudioNeedAudioProcess && this._localAudioTrack && localAudioWorkletNode) {
      this._localAudioTrack.addAudioProcessor(localAudioWorkletNode, mixNode, silentNode);
      this.audioReferenceMap.forEach((audioReference, refId) => {
        this._localAudioTrack?.mixAudioReference(audioReference, refId);
      });
    }
    if (this.isScreenAudioNeedAudioProcess && this._localScreenAudioTrack && screenAudioWorkletNode) {
      this._localScreenAudioTrack?.addAudioProcessor(screenAudioWorkletNode, mixNode, silentNode);
      this.audioReferenceMap.forEach((audioReference, refId) => {
        this._localScreenAudioTrack?.mixAudioReference(audioReference, refId);
      });
    }
  }
  removeDenoiser(node: AudioNode) {
    delete this.denoiser;
    return this._localAudioTrack?.removeDenoiser(node);
  }
  addVoiceChanger(head: AudioNode, tail: AudioNode) {
    this.voiceChanger = [head, tail];
    this._localAudioTrack?.pipeline.voiceChanger.setNode(head, tail);
  }
  removeVoiceChanger() {
    delete this.voiceChanger;
    this._localAudioTrack?.pipeline.voiceChanger.deleteNode();
  }
  removeAudioProcessor(localAudioWorkletNode?: AudioNode, screenAudioWorkletNode?: AudioNode) {
    delete this.audioProcessor;
    this._localAudioTrack?.removeAudioProcessor(localAudioWorkletNode);
    this._localScreenAudioTrack?.removeAudioProcessor(screenAudioWorkletNode);
  }
  destroy() {
    this.close();
    this.audioReferenceMap.clear();
    this.getPCMAbortCtrlMap.forEach(abortCtrl => abortCtrl?.abort('destroy'));
    this.getPCMAbortCtrlMap.clear();
    this.audioFrameEventConfigMap.clear();
    this.uninstallEvent();
  }

  addEncodeProcessor({ processor, type }: { processor: AudioEncodeProcessor, type: AudioCodecPipelineType; }) {
    if (this.encodePipeline.includes(processor)) return;
    this.encodePipeline[type] = processor;
    this.room?.enableInsertableStreams();
  }
  addDecodeProcessor({ processor, type }: { processor: AudioDecodeProcessor, type: AudioCodecPipelineType; }) {
    if (this.decodePipeline.includes(processor)) return;
    this.decodePipeline[type] = processor;
    this.room?.enableInsertableStreams();
  }
  removeEncodeProcessor({ type }: { type: AudioCodecPipelineType; }) {
    this.encodePipeline[type] = void 0;
  }
  removeDecodeProcessor({ type }: { type: AudioCodecPipelineType; }) {
    this.decodePipeline[type] = void 0;
  }
  handleLocalTrackStarted({ room, userId }: {room: IRoom; userId: string; }) {
    if (room !== this.room) return;
    if (this.getPCMAbortCtrlMap.get(userId)) return;
    const getPCMAbortCtrl = this.getPCM(datas => {
      this.room?.emit(RoomEvent.AUDIO_FRAME, datas);
    }, '');
    this.getPCMAbortCtrlMap.set(userId, getPCMAbortCtrl);
    if (this.getPCMAbortCtrlMap.get(userId)) {
      this._localAudioTrack?.on(TrackEvent.INPUT_MEDIA_TRACK_CHANGED, () => {
        let getPCMAbortCtrl = this.getPCMAbortCtrlMap.get(userId);
        if (getPCMAbortCtrl) {
          getPCMAbortCtrl.abort('inputMediaTrackChanged');
          getPCMAbortCtrl = this.getPCM(datas => {
            this.room?.emit(RoomEvent.AUDIO_FRAME, datas);
          }, '');
          this.getPCMAbortCtrlMap.set(userId, getPCMAbortCtrl);
        }
      });
    }
  }
  handleLocalTrackStopped({ room, userId }: {room: IRoom; userId: string; }) {
    if (room !== this.room) return;
    const getPCMAbortCtrl = this.getPCMAbortCtrlMap.get(userId);
    if (getPCMAbortCtrl) {
      getPCMAbortCtrl.abort('stopLocalAudio');
      this.getPCMAbortCtrlMap.delete(userId);
    }
  }
  handleRemoteTrackStarted({ room, userId }: {room: IRoom; userId: string; }) {
    if (room !== this.room) return;
    if (!this.getPCMAbortCtrlMap.get(userId)) {
      const abortCtrl = this.room.audioManager.getPCM(datas => {
        this.room?.emit(RoomEvent.AUDIO_FRAME, datas);
      }, userId);
      this.getPCMAbortCtrlMap.set(userId, abortCtrl);
    }
  }
  handleRemoteTrackStopped({ room, userId }: {room: IRoom; userId: string; }) {
    if (room !== this.room) return;
    const getPCMAbortCtrl = this.getPCMAbortCtrlMap.get(userId);
    if (getPCMAbortCtrl) {
      getPCMAbortCtrl.abort('stopRemoteAudio');
      this.getPCMAbortCtrlMap.delete(userId);
    }
  }
  installEvent() {
    innerEmitter.on(INNER_EVENT.LOCAL_AUDIO_STARTED, this.handleLocalTrackStarted, this);
    innerEmitter.on(INNER_EVENT.LOCAL_AUDIO_STOPPED, this.handleLocalTrackStopped, this);
    innerEmitter.on(INNER_EVENT.REMOTE_AUDIO_STARTED, this.handleRemoteTrackStarted, this);
    innerEmitter.on(INNER_EVENT.REMOTE_AUDIO_STOPPED, this.handleRemoteTrackStopped, this);
  }
  uninstallEvent() {
    innerEmitter.off(INNER_EVENT.LOCAL_AUDIO_STARTED, this.handleLocalTrackStarted);
    innerEmitter.off(INNER_EVENT.LOCAL_AUDIO_STOPPED, this.handleLocalTrackStopped);
    innerEmitter.off(INNER_EVENT.REMOTE_AUDIO_STARTED, this.handleRemoteTrackStarted);
    innerEmitter.off(INNER_EVENT.REMOTE_AUDIO_STOPPED, this.handleRemoteTrackStopped);
  }

  updateAudioReference({ type, audioReference, refId, volume }: { type: AudioReferenceChangeType, refId: string, audioReference?: AudioSource; volume?: number }) {
    if (type === AudioReferenceChangeType.ADD) {
      if (this.audioReferenceMap.get(refId) || !audioReference) return;
      this.audioReferenceMap.set(refId, audioReference);
      if (!this.audioProcessor) return; // 没开启插件不需要混合
      this.mixAudioReference(audioReference, refId);
    } else if (type === AudioReferenceChangeType.REMOVE) {
      const audioReference = this.audioReferenceMap.get(refId);
      if (audioReference) {
        this.audioReferenceMap.delete(refId);
        this.unMixAudioReference(refId);
      }
    } else if (type === AudioReferenceChangeType.UPDATE_VOLUME) {
      if (!this.audioProcessor || isUndefined(volume)) return; // 没开启插件不需要调整音量. TODO: 缓存开启插件前的音量
      this.setAudioReferenceVolume(refId, volume);
    }
  }
}

